import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabaseClient } from '../supabase';

/**
 * Live "someone else is editing this" locks, built on Supabase presence.
 *
 * Presence rather than broadcast: a lock is part of the holder's tracked state, so when a
 * tab closes, crashes or drops its socket the server removes the presence entry and every
 * other client sees the lock disappear on the next sync. Nothing has to notice the
 * disconnect and clean up after it, which is what the old broadcast-based cell locks
 * needed a separate presence-leave listener for (and still leaked locks whenever the two
 * disagreed on a user's display name).
 *
 * One channel per table, shared by every consumer in the tab (the grid and any side panel
 * showing a record from that table), so a cell lock and a record lock see each other.
 */

export interface EditLock {
  rowId: string;
  /** null = the whole record (side panel edit mode); otherwise a single grid column. */
  colId: string | null;
}

export interface RemoteLock extends EditLock {
  userName: string;
  sessionId: string;
}

/** One id per browser tab: the presence key, and how we tell our own locks apart from everyone else's. */
export const TAB_SESSION_ID = Math.random().toString(36).slice(2, 10);

export const lockKey = (rowId: string | number, colId?: string | null): string =>
  `${rowId}::${colId ?? ''}`;

export const editLockTopic = (schema: string, table: string): string =>
  `edit_locks_${schema}_${table}`;

interface Registration {
  channel: RealtimeChannel;
  /** Number of live consumers; the channel is torn down when this hits zero. */
  refCount: number;
  /** Locks this tab holds on this table, keyed by lockKey(). */
  myLocks: Map<string, EditLock>;
  /** Locks held by other sessions, rebuilt from presence state on every sync. */
  remoteLocks: RemoteLock[];
  listeners: Set<() => void>;
  userName: string;
  subscribed: boolean;
}

const registry = new Map<string, Registration>();

/**
 * Push our current locks onto the channel.
 *
 * Releasing tracks an empty array rather than calling untrack(): against this Realtime
 * server untrack() does not produce a leave diff for the other clients, so an untracked
 * lock stays visible to everyone else until the tab actually disconnects. Tracking an
 * empty list overwrites our presence payload, which does propagate.
 */
const publish = (reg: Registration): void => {
  if (!reg.subscribed) return;
  void reg.channel.track({
    sessionId: TAB_SESSION_ID,
    userName: reg.userName,
    locks: [...reg.myLocks.values()],
  });
};

const rebuildRemoteLocks = (reg: Registration): void => {
  const state = reg.channel.presenceState<Record<string, unknown>>();
  const next: RemoteLock[] = [];

  Object.values(state).forEach((presences) => {
    (presences as any[]).forEach((presence) => {
      if (!presence || presence.sessionId === TAB_SESSION_ID) return;
      const userName: string = presence.userName || 'Another user';
      (presence.locks as EditLock[] | undefined)?.forEach((lock) => {
        if (!lock || lock.rowId === undefined || lock.rowId === null) return;
        next.push({
          rowId: String(lock.rowId),
          colId: lock.colId ?? null,
          userName,
          sessionId: presence.sessionId,
        });
      });
    });
  });

  reg.remoteLocks = next;
  reg.listeners.forEach((listener) => listener());
};

const acquireRegistration = (schema: string, table: string, userName: string): Registration => {
  const topic = editLockTopic(schema, table);
  const existing = registry.get(topic);
  if (existing) {
    existing.refCount += 1;
    // A later consumer may know the display name before an earlier one did.
    if (userName && userName !== existing.userName) {
      existing.userName = userName;
      publish(existing);
    }
    return existing;
  }

  const channel = supabaseClient.channel(topic, {
    // enabled:true is required by the Realtime server — without it the join is accepted but
    // no presence_state is ever sent, presenceState() stays {} forever and every client
    // thinks nobody else is editing. realtime-js 2.10.x doesn't send the flag itself, hence
    // the cast; drop it once the client version sets it.
    config: { presence: { key: TAB_SESSION_ID, enabled: true } as { key: string } },
  });

  const reg: Registration = {
    channel,
    refCount: 1,
    myLocks: new Map(),
    remoteLocks: [],
    listeners: new Set(),
    userName,
    subscribed: false,
  };

  channel
    .on('presence', { event: 'sync' }, () => rebuildRemoteLocks(reg))
    .subscribe((status, err) => {
      if (status === 'SUBSCRIBED') {
        reg.subscribed = true;
        // Locks taken while the join was still in flight have to be published now.
        publish(reg);
        return;
      }
      if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        reg.subscribed = false;
        if (err) console.error(`Edit lock channel ${topic} error:`, err);
        // Our view of other sessions is stale once the socket is gone. Drop the locks
        // rather than leave cells blocked on information we can no longer refresh.
        if (reg.remoteLocks.length > 0) {
          reg.remoteLocks = [];
          reg.listeners.forEach((listener) => listener());
        }
      }
    });

  registry.set(topic, reg);
  return reg;
};

const releaseRegistration = (schema: string, table: string): void => {
  const topic = editLockTopic(schema, table);
  const reg = registry.get(topic);
  if (!reg) return;
  reg.refCount -= 1;
  if (reg.refCount > 0) return;
  registry.delete(topic);
  void reg.channel.unsubscribe();
  supabaseClient.removeChannel(reg.channel);
};

/**
 * Who else is editing this, or null if it is free.
 *
 * A record lock (colId null) covers every column of that row, and a cell lock blocks a
 * record-level request — asking about the record means asking about all of it.
 */
const findLock = (
  reg: Registration,
  rowId: string | number,
  colId?: string | null
): RemoteLock | null => {
  const row = String(rowId);
  for (const lock of reg.remoteLocks) {
    if (lock.rowId !== row) continue;
    if (!lock.colId || !colId || lock.colId === colId) return lock;
  }
  return null;
};

export const editLocksInternals = {
  acquireRegistration,
  releaseRegistration,
  findLock,
  publish,
};
